// Een shift in uitvoering bewaren, zodat je na het sluiten of verversen van de app verder kunt.
// Staat los van de spelstand (eigen sleutel): kapotte of verouderde data wordt genegeerd, nooit geraden.
import type { GameEvent, EventKind, EventPhase } from '../game/events';
import { SHIFT_SECONDS, type SavedOrder, type ShiftSnapshot } from '../game/shift';

export const SHIFT_SAVE_KEY = 'bakenbezorg_shift_v1';
/** Een bewaarde shift die ouder is dan dit (ms) bieden we niet meer aan. */
export const SHIFT_SAVE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface SavedShift {
  version: 1;
  savedAt: number;
  daily: boolean;
  rank: number;
  snapshot: ShiftSnapshot;
  scooter: { x: number; y: number; angle: number };
  streetMoney: number;
  shiftDistance: number;
  finedThisShift: boolean;
  starsAtStart: number;
}

const STATUSES = ['aangeboden', 'geaccepteerd', 'ingeladen', 'bezorgd', 'verlopen', 'mislukt', 'verloren'];
const KINDS: EventKind[] = ['bui', 'mist', 'markt'];
const PHASES: EventPhase[] = ['wacht', 'gewaarschuwd', 'bezig', 'voorbij'];

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function parseOrder(v: unknown): SavedOrder | null {
  if (!isObj(v)) return null;
  if (typeof v.id !== 'string' || typeof v.productId !== 'string' || typeof v.destId !== 'string') return null;
  if (typeof v.customer !== 'string' || !STATUSES.includes(v.status)) return null;
  const nums = [v.offerExpiresAt, v.deadlineSec, v.temp, v.integrity, v.expectedPay];
  if (!nums.every(isNum)) return null;
  if (v.loadedAt !== null && !isNum(v.loadedAt)) return null;
  return {
    id: v.id,
    productId: v.productId,
    destId: v.destId,
    customer: v.customer.slice(0, 80),
    status: v.status,
    offerExpiresAt: v.offerExpiresAt,
    deadlineSec: clamp(v.deadlineSec, 1, 3600),
    loadedAt: v.loadedAt,
    temp: clamp(v.temp, 0, 100),
    integrity: clamp(v.integrity, 0, 100),
    expectedPay: clamp(v.expectedPay, 0, 1000),
    race: v.race === true,
  };
}

function parseEvent(v: unknown): GameEvent | null {
  if (!isObj(v) || typeof v.id !== 'string') return null;
  if (!KINDS.includes(v.kind) || !PHASES.includes(v.phase)) return null;
  if (![v.warnAt, v.startsAt, v.endsAt].every(isNum)) return null;
  const c = v.closure;
  if (c !== undefined) {
    const ok = isObj(c) && typeof c.id === 'string' && (c.axis === 'h' || c.axis === 'v') && typeof c.street === 'string' && typeof c.name === 'string' && [c.x, c.y, c.w, c.h].every(isNum);
    if (!ok) return null;
  }
  return v as GameEvent;
}

/** Controleert een opgeslagen shift. Geeft null bij alles wat niet klopt of te oud is. */
export function parseSavedShift(input: unknown, now = Date.now()): SavedShift | null {
  if (!isObj(input) || input.version !== 1) return null;
  if (!isNum(input.savedAt) || now - input.savedAt > SHIFT_SAVE_MAX_AGE_MS || input.savedAt > now + 60_000) return null;
  const s = input.snapshot;
  if (!isObj(s)) return null;
  const nums = [s.t, s.combo, s.bestCombo, s.earnings, s.stars, s.raceWins, s.nextOfferAt];
  if (!nums.every(isNum) || s.t < 0 || s.t >= SHIFT_SECONDS) return null;
  if (s.brideAt !== null && !isNum(s.brideAt)) return null;
  if (!Array.isArray(s.orders) || !Array.isArray(s.events) || !Array.isArray(s.delivered)) return null;
  if (s.orders.length > 200 || s.events.length > 10 || s.delivered.length > 200) return null;
  const orders = s.orders.map(parseOrder);
  const events = s.events.map(parseEvent);
  if (orders.some((o) => !o) || events.some((e) => !e)) return null;
  const delivered: ShiftSnapshot['delivered'] = [];
  for (const d of s.delivered) {
    if (!isObj(d) || typeof d.orderId !== 'string' || !isObj(d.result)) return null;
    const r = d.result;
    if (![r.timeScore, r.careScore, r.score, r.stars, r.pay, r.tip, r.total].every(isNum)) return null;
    if (r.tempScore !== null && !isNum(r.tempScore)) return null;
    delivered.push({ orderId: d.orderId, result: r as any });
  }
  const sc = input.scooter;
  if (!isObj(sc) || ![sc.x, sc.y, sc.angle].every(isNum)) return null;
  if (!isNum(input.rank) || !isNum(input.streetMoney) || !isNum(input.shiftDistance) || !isNum(input.starsAtStart)) return null;
  return {
    version: 1,
    savedAt: input.savedAt,
    daily: input.daily === true,
    rank: input.rank,
    snapshot: {
      t: s.t,
      combo: Math.max(0, s.combo),
      bestCombo: Math.max(0, s.bestCombo),
      earnings: s.earnings,
      stars: Math.max(0, s.stars),
      raceWins: Math.max(0, s.raceWins),
      nextOfferAt: s.nextOfferAt,
      brideAt: s.brideAt,
      events: events as GameEvent[],
      orders: orders as SavedOrder[],
      delivered,
    },
    scooter: { x: sc.x, y: sc.y, angle: sc.angle },
    streetMoney: input.streetMoney,
    shiftDistance: Math.max(0, input.shiftDistance),
    finedThisShift: input.finedThisShift === true,
    starsAtStart: Math.max(0, input.starsAtStart),
  };
}

export function readSavedShift(storage: Pick<Storage, 'getItem'> | null, now = Date.now()): SavedShift | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SHIFT_SAVE_KEY);
    return raw ? parseSavedShift(JSON.parse(raw), now) : null;
  } catch {
    return null;
  }
}

export function writeSavedShift(storage: Pick<Storage, 'setItem'> | null, saved: SavedShift): boolean {
  if (!storage) return false;
  try {
    storage.setItem(SHIFT_SAVE_KEY, JSON.stringify(saved));
    return true;
  } catch {
    return false;
  }
}

export function clearSavedShift(storage: Pick<Storage, 'removeItem'> | null) {
  try {
    storage?.removeItem(SHIFT_SAVE_KEY);
  } catch {
    /* opslag geblokkeerd: niets te wissen */
  }
}
